/**
 * AccessibleModal - Extended Bootstrap Modal with Background Content Isolation
 * Uses global Bootstrap object instead of ES6 imports
 */

import { ModalAccessibilityManager } from './ModalAccessibilityManager';
import { ModalFocusManager } from './ModalFocusManager';
import { ModalDOMManager } from './ModalDOMManager';
import {
    AccessibleModalConfig,
    BootstrapModalInstance,
    BootstrapModalConstructor
} from './ModalInterfaces';

function debugBackdrop(action: string) {
    const backdrops = document.querySelectorAll('.modal-backdrop');
    console.log(`${action}: Found ${backdrops.length} backdrop elements`);
    backdrops.forEach((backdrop, index) => {
        console.log(`Backdrop ${index}:`, {
            classes: backdrop.className,
            style: backdrop.getAttribute('style'),
            parent: backdrop.parentElement?.tagName,
            display: getComputedStyle(backdrop).display,
            opacity: getComputedStyle(backdrop).opacity
        });
    });
}


// Monitor backdrop creation
const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
                const element = node as Element;
                if (element.classList && element.classList.contains('modal-backdrop')) {
                    console.log('BACKDROP CREATED:', {
                        classes: element.className,
                        stackTrace: new Error().stack
                    });
                    debugBackdrop('Backdrop just created');
                }
            }
        });
    });
});

// Start observing
observer.observe(document.body, { childList: true });

class AccessibleModal {
    public _bootstrapModal: BootstrapModalInstance;
    private _element: Element;
    private _accessibilityManager: ModalAccessibilityManager;
    private _focusManager: ModalFocusManager;
    private _domManager: ModalDOMManager;

    constructor(element: Element, config?: AccessibleModalConfig) {
        this._element = element;

        // Create manager instances
        this._accessibilityManager = new ModalAccessibilityManager(element);
        this._focusManager = new ModalFocusManager(element);
        this._domManager = new ModalDOMManager(element);

        // Don't create our own Bootstrap modal - just get the existing one
        this._bootstrapModal = window.bootstrap.Modal.getInstance(element) ||
            window.bootstrap.Modal.getOrCreateInstance(element, { focus: false });
    }

    /**
     * Override Bootstrap's show method to add background isolation
     */
    public show(relatedTarget?: Element): void {
        // Move modal to body level before showing (if not already there)
        this._domManager.moveToBody();

        // Call original Bootstrap show
        this._bootstrapModal.show(relatedTarget);

        // Add background isolation and focus management using Bootstrap's shown event
        const onShown = () => {
            this._accessibilityManager.isolateBackgroundContent();
            // Focus the close button after isolation is applied
            this._focusManager.focusCloseButton();
            this._element.removeEventListener('shown.bs.modal', onShown);
        };
        this._element.addEventListener('shown.bs.modal', onShown);
    }

    /**
     * Override Bootstrap's hide method to restore background content
     */
    public hide(): void {
        debugBackdrop('BEFORE cleanup');

        // Remove focus trap and restore background content before hiding
        this._focusManager.removeFocusTrap();
        this._accessibilityManager.restoreBackgroundContent();

        debugBackdrop('BEFORE Bootstrap hide');

        // Call original Bootstrap hide
        this._bootstrapModal.hide();

        // Add a timeout to check after Bootstrap should be done
        setTimeout(() => {
            debugBackdrop('AFTER Bootstrap hide (500ms delay)');
        }, 500);
    }

    /**
     * Dispose method - clean up and delegate to Bootstrap
     */
    public dispose(): void {
        this._focusManager.dispose();
        this._accessibilityManager.dispose();
        this._bootstrapModal.dispose();
    }

    /**
     * Toggle method - delegate to Bootstrap
     */
    public toggle(relatedTarget?: Element): void {
        this._bootstrapModal.toggle(relatedTarget);
    }

    // Public methods to maintain backward compatibility with global event handlers
    public _isolateBackgroundContent(): void {
        this._accessibilityManager.isolateBackgroundContent();
    }

    public _restoreBackgroundContent(): void {
        this._accessibilityManager.restoreBackgroundContent();
    }

    public _removeFocusTrap(): void {
        this._focusManager.removeFocusTrap();
    }

    // Static methods to maintain Bootstrap API compatibility
    public static getInstance(element: Element): AccessibleModal | null {
        return null;
    }

    public static getOrCreateInstance(element: Element, config?: AccessibleModalConfig): AccessibleModal {
        return new AccessibleModal(element, config);
    }
}


// Export for use in other modules
export default AccessibleModal;

// Also make it available globally for direct use
(window as any).AccessibleModal = AccessibleModal;

// Global modal instance registry
const modalRegistry = new Map<Element, AccessibleModal>();

// Auto-initialize: Override Bootstrap's data API when the script loads
document.addEventListener('DOMContentLoaded', function () {
    // Let Bootstrap handle the modal triggers naturally
    // We'll enhance modals when they're shown

    document.addEventListener('show.bs.modal', function (e: Event) {
        const modalElement = e.target as Element;
        console.log('Bootstrap about to show modal:', modalElement.id);

        // Get or create AccessibleModal instance just for enhancement
        let accessibleModal = modalRegistry.get(modalElement);
        if (!accessibleModal) {
            console.log('Creating AccessibleModal wrapper for Bootstrap modal');
            accessibleModal = new AccessibleModal(modalElement);
            modalRegistry.set(modalElement, accessibleModal);
        }

        // Move to body level before Bootstrap processes it
        if (modalElement.parentElement !== document.body) {
            modalElement.remove();
            document.body.appendChild(modalElement);
        }
    });

    document.addEventListener('shown.bs.modal', function (e: Event) {
        const modalElement = e.target as Element;
        const accessibleModal = modalRegistry.get(modalElement);
        if (accessibleModal) {
            console.log('Applying accessibility enhancements');
            accessibleModal._isolateBackgroundContent();
        }
    });

    document.addEventListener('hide.bs.modal', function (e: Event) {
        const modalElement = e.target as Element;
        const accessibleModal = modalRegistry.get(modalElement);
        if (accessibleModal) {
            console.log('Cleaning up accessibility enhancements');
            accessibleModal._removeFocusTrap();
            accessibleModal._restoreBackgroundContent();
        }
    });

    console.log('AccessibleModal initialized to work WITH Bootstrap');
});